/**
 * `src/lib/ui/reader/parts.ts` — the word-span contract, at the render layer.
 *
 * PURE. No DOM, no Svelte. `AyahBlock.svelte` is a `{#each}` over what this
 * returns and contains no text logic of its own, which is the point: the thing
 * that decides which characters get a `data-w` is a plain function with a test
 * over all 6,236 ayat, not a template.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE RULE THAT MATTERS MOST IN M2
 * ═══════════════════════════════════════════════════════════════════════════
 * Canonical word index 0 of an ayah is the first word AFTER the prepended
 * basmala on the 112 rows that carry one. So:
 *
 *   - the basmala comes out as its own string, rendered as its own block-level
 *     element, carrying NO index, NO `Pos` and NO `data-w`;
 *   - `t.split(/\s+/)` appears nowhere, here or in any component;
 *   - the number of anchorable word pieces is asserted, per ayah, against BOTH
 *     `model.wordCount` (which `$lib/content` already proved equals the row's
 *     `w`) AND `wordCountOf(ayahId)` read fresh out of the FROZEN fixture.
 *
 * The second assertion is not redundant belt-and-braces. `buildAyahModel` runs
 * in the content layer and checks the model it builds; this runs in the render
 * layer and checks the model it was HANDED. A future caller that synthesises an
 * `AyahModel` — a test double, a search-result preview, a note-editor quote — is
 * exactly the path by which a four-word shift would reach a user, and it does
 * not go through `buildAyahModel`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHY GAPS ARE CARRIED AND NOT SYNTHESISED
 * ═══════════════════════════════════════════════════════════════════════════
 * `$lib/content`'s segments partition the verbatim text and the material
 * between them is whitespace ONLY (`assertSegmentsCoverText` proves it). This
 * module carries each gap verbatim onto the piece that follows it rather than
 * emitting `' '` between pieces, so the rendered DOM reassembles to `t`
 * character for character. `reassemble()` below is that claim as a function,
 * and `reader.spec.ts` runs it over the whole mushaf.
 *
 * That matters beyond pedantry: the mushaf's standalone waqf marks are separated
 * by real spaces in the source, a synthesised single space would silently
 * normalise any that are not, and the anchor's Layer-3 `text_hash_raw` is taken
 * over the verbatim span. A renderer that shows normalised text while the hash
 * is over unnormalised text is a drift report waiting to happen.
 */

import { posOf, wordCountOf, type AyahId, type Pos } from '$lib/anchor';
import type { AyahModel } from '$lib/content';

/** An anchorable canonical word. The ONLY piece kind that carries an index. */
export interface WordPiece {
	kind: 'word';
	/** verbatim token text. */
	text: string;
	/** 0-based canonical word index — what a `ref` names. */
	index: number;
	/** `posOf(ayahId, index)` — Layer 2 of the anchor. */
	pos: Pos;
	/** the verbatim whitespace that precedes this piece in `t`. */
	gap: string;
}

/** A standalone waqf or sajdah sign. Rendered, never anchorable, never indexed. */
export interface MarkPiece {
	kind: 'mark';
	text: string;
	gap: string;
}

export type AyahPiece = WordPiece | MarkPiece;

/** One ayah, ready to render. */
export interface AyahParts {
	ayahId: AyahId;
	surah: number;
	ayah: number;
	/**
	 * The prepended basmala, verbatim, on the 112 rows that carry one — and
	 * `null` everywhere else, INCLUDING al-Fatiha, where the basmala genuinely
	 * is ayah 1 and its four words are anchorable words 0..3 like any others.
	 */
	basmala: string | null;
	/** the body of the ayah: the words and the marks, in source order. */
	pieces: readonly AyahPiece[];
	/** `pieces.filter(word).length`, asserted equal to `wordCountOf(ayahId)`. */
	wordCount: number;
}

/** A render-layer refusal. Distinct from `$lib/content`'s errors: reaching this
 *  means a model got as far as a component with the wrong word count on it. */
export class ReaderContractError extends Error {
	readonly where: string;

	constructor(where: string, message: string) {
		super(`${message} (${where})`);
		this.name = 'ReaderContractError';
		this.where = where;
	}
}

/**
 * Turn a validated `AyahModel` into the exact list a component iterates.
 *
 * Throws `ReaderContractError` rather than rendering a model whose word count
 * disagrees with the frozen fixture. A reader that cannot supply a wordmap to
 * the canonical segmentation may not render word anchors (SPEC §3.2); this is
 * that sentence, at the last moment it can still be enforced.
 */
export function ayahParts(model: AyahModel): AyahParts {
	const where = `${model.surah}:${model.ayah}`;

	const frozenCount = wordCountOf(model.ayahId);
	if (model.wordCount !== frozenCount) {
		throw new ReaderContractError(
			where,
			`the model claims ${model.wordCount} canonical words, the frozen fixture says ` +
				`${frozenCount}; refusing to draw word spans over a segmentation the anchor ` +
				'space does not share'
		);
	}

	const basmala = model.basmala;
	const body =
		basmala === null ? model.segments : model.segments.filter((s) => s.kind !== 'basmala');

	/* The cursor starts after the basmala, so the space that separated the
	 * basmala from word 0 in the source line does not become a leading space in
	 * the paragraph. It is whitespace; dropping it is the one deviation from
	 * verbatim reassembly this module makes, and `reassemble()` accounts for it
	 * explicitly rather than pretending otherwise. */
	let cursor = basmala === null ? 0 : basmala.end;
	let leading = true;
	const pieces: AyahPiece[] = [];
	let expectedIndex = 0;

	for (const segment of body) {
		const gap = leading ? '' : model.text.slice(cursor, segment.start);
		leading = false;
		cursor = segment.end;

		if (segment.kind === 'word') {
			if (segment.index !== expectedIndex) {
				throw new ReaderContractError(
					where,
					`word segments are not in canonical order: expected index ${expectedIndex}, ` +
						`got ${segment.index}`
				);
			}
			const expectedPos = posOf(model.ayahId, segment.index);
			if (segment.pos !== expectedPos) {
				throw new ReaderContractError(
					where,
					`word ${segment.index} carries Pos ${segment.pos}, posOf() says ${expectedPos}`
				);
			}
			pieces.push({
				kind: 'word',
				text: segment.text,
				index: segment.index,
				pos: segment.pos,
				gap
			});
			expectedIndex++;
		} else if (segment.kind === 'mark') {
			pieces.push({ kind: 'mark', text: segment.text, gap });
		} else {
			/* A second basmala segment. `buildAyahModel` cannot emit one, so this
			 * is a synthesised model — refuse it rather than silently index past
			 * four words of scripture. */
			throw new ReaderContractError(where, 'more than one basmala segment in a single ayah');
		}
	}

	if (expectedIndex !== frozenCount) {
		throw new ReaderContractError(
			where,
			`produced ${expectedIndex} anchorable word spans for an ayah of ${frozenCount} ` +
				'canonical words'
		);
	}

	return {
		ayahId: model.ayahId,
		surah: model.surah,
		ayah: model.ayah,
		basmala: basmala === null ? null : basmala.text,
		pieces,
		wordCount: expectedIndex
	};
}

/** The identity half of an ayah, as `AyahBlock` stamps it on the container. */
export interface AyahIdentity {
	ayahId: AyahId;
	surah: number;
	ayah: number;
}

/**
 * Assert that a block and the word spans about to be drawn inside it describe
 * THE SAME AYAH.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHY THIS EXISTS: THE TWO HALVES OF A `ref` COME FROM TWO DIFFERENT ARRAYS
 * ═══════════════════════════════════════════════════════════════════════════
 * `AyahBlock` stamps `data-ayah-id` / `data-ayah` / `data-surah` on the
 * container from `block`, and `data-w` / `data-pos` on each word span from
 * `parts`. `ReaderList` pairs them positionally — `parts[row.index]` beside
 * `blocks[row.index]` — and its only statement of the invariant used to be a
 * doc comment saying "same length and order as `blocks`".
 *
 * A SHORTER `parts` array throws a `TypeError` at render, which is loud. A
 * same-length but MISALIGNED one renders silently and correctly-looking, and
 * M3's selection code — which walks these spans and must read the ayah off the
 * container, because `data-w` alone is meaningless — would mint refs for the
 * wrong ayah. `data-pos` would then disagree with `data-ayah-id` INSIDE A
 * SINGLE ELEMENT, and nothing in the document would say which to believe.
 *
 * The data needed to check it was already present and unused: `AyahParts`
 * carries `ayahId`, `surah` and `ayah`. So it is checked, in both places the
 * two arrays meet — once per navigation in `read/[surah]/+page.ts`, and once
 * per rendered row in `AyahBlock` itself, because the component is also
 * reachable from a test double or an M3 preview that never went through the
 * route's `load`.
 */
export function assertPartsMatch(parts: AyahParts, block: AyahIdentity): AyahParts {
	if (parts.ayahId !== block.ayahId || parts.surah !== block.surah || parts.ayah !== block.ayah) {
		throw new ReaderContractError(
			`${block.surah}:${block.ayah}`,
			`the block is ayahId ${block.ayahId} but its word spans came from ayahId ` +
				`${parts.ayahId} (${parts.surah}:${parts.ayah}); refusing to stamp one ayah's ` +
				'identity onto another ayah’s word indices'
		);
	}
	return parts;
}

/**
 * The rendered DOM, as a string, exactly as a component draws it.
 *
 * The spec walks all 6,236 ayat asserting `reassemble(ayahParts(model))` equals
 * the verbatim `t` — with the single documented exception of the one whitespace
 * run between a prepended basmala and canonical word 0, which becomes the
 * boundary between two elements instead of a character.
 */
export function reassemble(parts: AyahParts): string {
	let out = parts.basmala ?? '';
	let first = true;
	for (const piece of parts.pieces) {
		if (first && parts.basmala !== null) out += ' ';
		first = false;
		out += piece.gap + piece.text;
	}
	return out;
}
